import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@vimos/ui";
import {
  fetchAdminPriorityQueue,
  type AdminPriorityBucket,
  type AdminPriorityItem,
} from "../directory/directoryApi";

const PAGE_SIZE = 30;
const money = new Intl.NumberFormat("es-PE", { style: "currency", currency: "PEN" });
const dayFormat = new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeZone: "UTC" });
const timeFormat = new Intl.DateTimeFormat("es-PE", { dateStyle: "medium", timeStyle: "short" });

function todayLocal(): string {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
}

function formatDate(value: string | null, withTime = false): string {
  if (!value) return "Fecha pendiente";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Fecha pendiente";
  return (withTime ? timeFormat : dayFormat).format(parsed);
}

const bucketOptions: Array<{ id: AdminPriorityBucket; label: string }> = [
  { id: "all", label: "Todo" },
  { id: "urgent", label: "Urgente" },
  { id: "soon", label: "Próximo" },
  { id: "follow_up", label: "Seguimiento" },
];

function priorityLabel(priority: AdminPriorityItem["priority"]): string {
  if (priority === "urgent") return "Urgente";
  if (priority === "soon") return "Próximo";
  return "Seguimiento";
}

function priorityTone(priority: AdminPriorityItem["priority"]): "market" | "signal" | "neutral" {
  if (priority === "urgent") return "market";
  if (priority === "soon") return "signal";
  return "neutral";
}

function kindLabel(kind: AdminPriorityItem["kind"]): string {
  if (kind === "charge") return "COBRO";
  if (kind === "submission") return "SOLICITUD";
  if (kind === "publication") return "PUBLICACIÓN";
  return "TAREA INTERNA";
}

function itemDate(item: AdminPriorityItem): string {
  if (item.kind === "submission" || item.kind === "publication") {
    return `Recibido ${formatDate(item.activity_at, true)}`;
  }
  return item.due_on ? `Fecha objetivo ${formatDate(item.due_on)}` : "Sin fecha objetivo";
}

function itemAction(item: AdminPriorityItem): string {
  if (item.kind === "charge") return "Abrir cobros";
  if (item.kind === "submission") return "Revisar solicitud";
  if (item.kind === "publication") return "Abrir ficha 360";
  return "Gestionar seguimiento";
}

function QueueRow({ item }: { item: AdminPriorityItem }) {
  return (
    <article className="grid gap-4 border-b border-slate-100 px-4 py-4 last:border-b-0 sm:px-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] font-bold tracking-[0.12em] text-slate-400">{kindLabel(item.kind)}</span>
          <Badge tone={priorityTone(item.priority)}>{priorityLabel(item.priority)}</Badge>
          {item.origin ? <span className="text-xs text-slate-500">{item.origin === "saas" ? "SaaS" : "Manual"}</span> : null}
        </div>
        <h3 className="mt-2 truncate text-sm font-semibold text-slate-950">{item.title}</h3>
        <p className="mt-0.5 truncate text-sm text-slate-600">{item.store_name}</p>
        <p className="mt-1 text-xs leading-5 text-slate-500">{item.detail}</p>
        <p className="mt-1 text-xs text-slate-400">{itemDate(item)}</p>
      </div>
      <div className="flex items-center justify-between gap-3 lg:justify-end">
        {item.amount !== null ? <span className="text-sm font-semibold tabular-nums text-slate-800">{money.format(Number(item.amount))}</span> : null}
        <Link
          to={item.href}
          className="inline-flex h-9 shrink-0 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-800"
        >
          {itemAction(item)} <span className="ml-2" aria-hidden="true">→</span>
        </Link>
      </div>
    </article>
  );
}

export function PrioritiesPage() {
  const [bucket, setBucket] = useState<AdminPriorityBucket>("all");
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const today = todayLocal();

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchDraft.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [searchDraft]);

  const queue = useQuery({
    queryKey: ["admin_priority_queue", bucket, search, page, today],
    queryFn: () => fetchAdminPriorityQueue({ bucket, search, page, today }),
    refetchInterval: 60_000,
  });
  const counts = queue.data?.counts;
  const pageCount = Math.max(1, Math.ceil((queue.data?.filteredTotal ?? 0) / PAGE_SIZE));

  return (
    <div className="mx-auto max-w-[1500px] pb-8">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-blue-700">
          VIMOS DIGITAL <span className="mx-1 text-slate-300">/</span> OPERACIONES
        </p>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-950 sm:text-4xl">Prioridades</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-500">
              Una bandeja para saber qué atender primero entre cobros, solicitudes, publicaciones y seguimientos.
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-500">
            Actualización automática cada minuto
          </span>
        </div>
      </header>

      <section className="mt-5 grid gap-3 sm:grid-cols-3" aria-label="Resumen de prioridades">
        <SummaryCard label="Urgente" value={counts?.urgent} hint="Vencidos o fuera del tiempo de atención" tone="red" />
        <SummaryCard label="Próximo" value={counts?.soon} hint="Por vencer o revisar dentro del día" tone="blue" />
        <SummaryCard label="Seguimiento" value={counts?.follow_up} hint="Acciones programadas y fichas por publicar" tone="slate" />
      </section>

      <section className="mt-5 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:p-5">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-base font-semibold text-slate-950">Bandeja de trabajo</h2>
              <p className="mt-1 text-xs text-slate-500">Ordenada por prioridad y fecha; las tareas se resuelven desde su módulo.</p>
            </div>
            <label className="relative block w-full md:max-w-xs">
              <span className="sr-only">Buscar en prioridades</span>
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <circle cx="10.8" cy="10.8" r="6.8" /><path d="m16 16 4.5 4.5" />
              </svg>
              <input
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
                placeholder="Buscar negocio o tarea…"
                className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Filtrar por prioridad">
            {bucketOptions.map((option) => {
              const selected = bucket === option.id;
              const count = option.id === "all" ? counts?.total : counts?.[option.id];
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => { setBucket(option.id); setPage(1); }}
                  className={`inline-flex h-8 items-center gap-2 rounded-full border px-3 text-xs font-semibold transition ${selected ? "border-blue-200 bg-blue-50 text-blue-800" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}
                >
                  {option.label}<span className={selected ? "text-blue-600" : "text-slate-400"}>{count ?? "—"}</span>
                </button>
              );
            })}
          </div>
        </div>

        {queue.isLoading ? (
          <div className="p-10 text-center text-sm text-slate-500">Calculando prioridades…</div>
        ) : queue.error ? (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            No se pudo cargar la bandeja. {queue.error instanceof Error ? queue.error.message : "Revisa la conexión e inténtalo de nuevo."}
            <button type="button" onClick={() => void queue.refetch()} className="ml-2 font-semibold underline">Reintentar</button>
          </div>
        ) : queue.data?.items.length ? (
          <div aria-live="polite">
            {queue.data.items.map((item) => <QueueRow key={item.id} item={item} />)}
          </div>
        ) : (
          <div className="px-5 py-14 text-center">
            <span className="mx-auto grid h-11 w-11 place-items-center rounded-full bg-emerald-50 text-lg text-emerald-700" aria-hidden="true">✓</span>
            <h3 className="mt-3 text-sm font-semibold text-slate-900">No hay prioridades en esta vista</h3>
            <p className="mt-1 text-sm text-slate-500">Cuando algo requiera atención aparecerá aquí automáticamente.</p>
          </div>
        )}

        {!queue.error && queue.data?.items.length ? (
          <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 sm:px-5">
            <p className="text-xs text-slate-500">{queue.data.filteredTotal} tarea(s) · página {page} de {pageCount}</p>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page <= 1} className="h-8 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-40">Anterior</button>
              <button type="button" onClick={() => setPage((current) => Math.min(pageCount, current + 1))} disabled={page >= pageCount} className="h-8 rounded-md border border-slate-200 px-3 text-xs font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-40">Siguiente</button>
            </div>
          </footer>
        ) : null}
      </section>
      <p className="mt-3 text-xs leading-5 text-slate-400">
        Los negocios con cortesía no generan tareas de cobro si no tienen cargos pendientes. La bandeja solo muestra pagos con vencimiento dentro de los próximos siete días.
      </p>
    </div>
  );
}

function SummaryCard({ label, value, hint, tone }: { label: string; value: number | undefined; hint: string; tone: "red" | "blue" | "slate" }) {
  const styles = {
    red: "border-red-100 bg-red-50 text-red-900",
    blue: "border-blue-100 bg-blue-50 text-blue-900",
    slate: "border-slate-200 bg-white text-slate-900",
  }[tone];
  return (
    <div className={`rounded-xl border p-4 shadow-sm ${styles}`}>
      <p className="text-xs font-semibold opacity-75">{label}</p>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value ?? "—"}</p>
      <p className="mt-1 text-xs opacity-70">{hint}</p>
    </div>
  );
}
